import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Flex } from "antd";
import PageHeading from "components/PageHeading.component";
import { translate } from "shared/languages";

interface BackPageHeadingProps {
  title: string;
  /** Route the arrow returns to (the hub or grid the page was opened from). */
  backTo: string;
  /**
   * For a page with more than one parent: the arrow does what the browser's
   * Back button does, and `backTo` is used only when the page is the first
   * one opened in this tab. A pushed parent route would land on the wrong
   * page and add a history entry, so the next browser Back reopened this one.
   */
  historyBack?: boolean;
  extra?: ReactNode;
  /** Accessible name for the arrow. Defaults to "Back". */
  backLabel?: string;
}

// The page heading for every detail page reached from a launcher or grid
// (the Pengaturan sub-pages, the Analitik report pages): a back arrow, then
// the title. One component so the arrow is the same focusable text Button
// everywhere, rather than a bare icon on some pages and a Button on others.
const BackPageHeading = ({
  title,
  backTo,
  historyBack,
  extra,
  backLabel,
}: BackPageHeadingProps) => {
  const navigate = useNavigate();
  // react-router numbers its entries in history.state.idx, starting at 0 for
  // the page this tab first loaded and keeping the number on a replace (a tab
  // switch), so idx > 0 means an app page sits behind this one. The location
  // key can't tell: a replace on the first page gives it a fresh key too.
  const goBack = () =>
    historyBack && (window.history.state?.idx ?? 0) > 0
      ? navigate(-1)
      : navigate(backTo);
  return (
    <PageHeading
      title={
        <Flex gap={12} align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={goBack}
            aria-label={backLabel ?? translate("button.back")}
          />
          {title}
        </Flex>
      }
      extra={extra}
    />
  );
};

export default BackPageHeading;
